<template>
    <div class="main">
        <div class="body-content">
            <h1>Generate LEO Excel</h1>
            <div class="form-group">
                <div class="form-item">
                    <label for="fromdate">From Date *</label>
                    <input type="text" id="fromdate" v-model="filters.fromdate" placeholder="dd/mm/yyyy"
                        v-on:blur="checkIsDate()" style="width:100%" />
                </div>
                <div class="form-item">
                    <label for="todate">To Date *</label>
                    <input type="text" id="todate" v-model="filters.todate" placeholder="dd/mm/yyyy"
                        v-on:blur="checkIsToDate()" style="width:100%" />
                </div>
                <div class="form-item">
                    <label>Report Type *</label>
                    <div class="radio-group">
                        <label class="radio-label">
                            <input type="radio" v-model="filters.reportType" value="0" />
                            <span>Consolidated</span>
                        </label>
                        <label class="radio-label">
                            <input type="radio" v-model="filters.reportType" value="1" />
                            <span>Separated</span>
                        </label>
                    </div>
                </div>
            </div>
            <div class="form-item">
                <button type="button" class="excel-button" @click="generateLeoExcel" :disabled="isSubmitting">
                    Generate LEO Excel
                </button>
            </div>
            <div class="form-item" v-if="message">
                <div :class="messageClass">{{ message }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
    name: 'GenerateLeoExcel',
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            filters: {
                fromdate: '',
                todate: '',
                reportType: '0'
            },
            isSubmitting: false,
            message: '',
            messageClass: ''
        };
    },
    methods: {
        isValidDate(dateString) {
            const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
            if (!datePattern.test(dateString)) {
                return false;
            }

            const [day, month, year] = dateString.split('/').map(Number);
            
            // Check if month is valid
            if (month < 1 || month > 12) {
                return false;
            }

            // Check if day is valid for the given month
            const daysInMonth = new Date(year, month, 0).getDate();
            if (day < 1 || day > daysInMonth) {
                return false;
            }

            // Check if year is reasonable (between 2000 and 2100)
            if (year < 2000 || year > 2100) {
                return false;
            }

            return true;
        },
        parseDate(dateString) {
            const [day, month, year] = dateString.split('/').map(Number);
            return new Date(year, month - 1, day);
        },
        checkIsDate() {
            if (!this.filters.fromdate) return;
            
            if (!this.isValidDate(this.filters.fromdate)) {
                this.toast.error('Invalid From Date. Please enter a valid date in dd/mm/yyyy format');
                this.filters.fromdate = '';
                return;
            }

            // Check if from date is not in the future
            const fromDate = this.parseDate(this.filters.fromdate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (fromDate > today) {
                this.toast.error('From Date cannot be in the future');
                this.filters.fromdate = '';
                return;
            }

            // If to date is already set, validate the range
            if (this.filters.todate && this.isValidDate(this.filters.todate)) {
                const toDate = this.parseDate(this.filters.todate);
                if (fromDate > toDate) {
                    this.toast.error('From Date cannot be after To Date');
                    this.filters.fromdate = '';
                    return;
                }
                
                // Check if date range exceeds 7 days
                const diffTime = Math.abs(toDate - fromDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays > 7) {
                    this.toast.error('Date range cannot exceed 7 days');
                    this.filters.fromdate = '';
                }
            }
        },
        checkIsToDate() {
            if (!this.filters.todate) return;
            
            if (!this.isValidDate(this.filters.todate)) {
                this.toast.error('Invalid To Date. Please enter a valid date in dd/mm/yyyy format');
                this.filters.todate = '';
                return;
            }

            // Check if to date is not in the future
            const toDate = this.parseDate(this.filters.todate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (toDate > today) {
                this.toast.error('To Date cannot be in the future');
                this.filters.todate = '';
                return;
            }

            // If from date is already set, validate the range
            if (this.filters.fromdate && this.isValidDate(this.filters.fromdate)) {
                const fromDate = this.parseDate(this.filters.fromdate);
                if (toDate < fromDate) {
                    this.toast.error('To Date cannot be before From Date');
                    this.filters.todate = '';
                    return;
                }
                
                // Check if date range exceeds 7 days
                const diffTime = Math.abs(toDate - fromDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays > 7) {
                    this.toast.error('Date range cannot exceed 7 days');
                    this.filters.todate = '';
                }
            }
        },
        async generateLeoExcel() {
            if (!this.filters.fromdate || !this.filters.todate) {
                this.message = 'Please enter both From Date and To Date';
                this.messageClass = 'error-message';
                return;
            }

            // Validate dates before submission
            if (!this.isValidDate(this.filters.fromdate)) {
                this.message = 'Invalid From Date. Please enter a valid date in dd/mm/yyyy format';
                this.messageClass = 'error-message';
                return;
            }

            if (!this.isValidDate(this.filters.todate)) {
                this.message = 'Invalid To Date. Please enter a valid date in dd/mm/yyyy format';
                this.messageClass = 'error-message';
                return;
            }

            // Check date range
            const fromDate = this.parseDate(this.filters.fromdate);
            const toDate = this.parseDate(this.filters.todate);

            
            if (fromDate > toDate) {
                this.message = 'From Date cannot be after To Date';
                this.messageClass = 'error-message';
                return;
            }

            // Check if date range exceeds 7 days
            const diffTime = Math.abs(toDate - fromDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays > 7) {
                this.message = 'Date range cannot exceed 7 days';
                this.messageClass = 'error-message';
                return;
            }

            this.isSubmitting = true;
            this.message = '';

            try {
                const accessToken = localStorage.getItem('access_token');
                const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

                const response = await axios.get('generate_leo_excel', {
                    params: {
                        fromdate: this.filters.fromdate,
                        todate: this.filters.todate,
                        reporttype: this.filters.reportType
                    },
                    headers,
                    responseType: 'blob'
                });

                // Check if response is JSON (error response) instead of blob (Excel file)
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    // Convert blob to JSON to read the error message
                    const text = await response.data.text();
                    const jsonResponse = JSON.parse(text);
                    
                    if (jsonResponse.status === 0) {
                        this.message = jsonResponse.message || 'No records found for the given date range';
                        this.messageClass = 'error-message';
                        this.toast.warning(jsonResponse.message || 'No records found for the given date range');
                        return;
                    }
                }

                // Create a download link for the Excel file
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `LEO_Report_${this.filters.fromdate.replace(/\//g, '-')}_to_${this.filters.todate.replace(/\//g, '-')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();

                this.message = 'LEO Excel generated successfully!';
                this.messageClass = 'success-message';
                this.toast.success('LEO Excel generated successfully!');
            } catch (error) {
                console.error('Error generating LEO Excel:', error);
                this.message = 'Error generating LEO Excel. Please try again.';
                this.messageClass = 'error-message';
                this.toast.error('Error generating LEO Excel. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        }
    }
};
</script>

<style scoped>
.main {
    min-height: 100vh;
    background-color: #f5f5f5;
    padding: 20px;
}

.body-content {
    max-width: 800px;
    margin: 0 auto;
    background: white;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

h1 {
    color: #333;
    margin-bottom: 30px;
    font-size: 24px;
}

.form-group {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-bottom: 20px;
}

.form-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
}

.form-item label {
    margin-bottom: 5px;
    font-weight: 500;
    color: #555;
}

.form-item input {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.form-item input:focus {
    outline: none;
    border-color: #4CAF50;
}

.radio-group {
    display: flex;
    gap: 20px;
    margin-top: 5px;
}

.radio-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-weight: normal;
    margin: 0;
}

.radio-label input[type="radio"] {
    margin-right: 8px;
    cursor: pointer;
    width: auto;
    accent-color: #4CAF50;
}

.radio-label span {
    color: #555;
}

.radio-label:hover span {
    color: #333;
}

.excel-button {
    background-color: #4CAF50;
    color: white;
    padding: 12px 24px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    transition: background-color 0.3s;
}

.excel-button:hover:not(:disabled) {
    background-color: #45a049;
}

.excel-button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}

.success-message {
    padding: 10px;
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
    border-radius: 4px;
    margin-top: 10px;
}

.error-message {
    padding: 10px;
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    margin-top: 10px;
}
</style>
